/**
 * DocumentStudio — Main container for the AI Document Beautification Pipeline
 *
 * Three views: list (previous sessions), input (create new), preview (side-by-side)
 * Pipeline pauses after suggestion generation for interactive image workflow.
 */

import { useState, useCallback, useEffect } from 'react'
import {
  ArrowLeft,
  Plus,
  Trash2,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  Copy,
  Check,
  Image,
  FileText,
} from 'lucide-react'
import { api } from '@/lib/api'
import { notify } from '@/lib/notify'
import type { StudioSessionDetail, StudioSessionSummary, StudioVisualization } from '@/lib/api'
import { StudioInput } from './StudioInput'
import { StudioSideBySide } from './StudioSideBySide'
import { StudioProgressOverlay } from './StudioProgressOverlay'
import { StudioExportMenu } from './StudioExportMenu'
import { StudioImageSuggestions } from './StudioImageSuggestions'
import { StudioCustomImageGenerator } from './StudioCustomImageGenerator'

type View = 'list' | 'input' | 'preview'
type PreviewTab = 'document' | 'images'

export function DocumentStudio() {
  const [view, setView] = useState<View>('list')
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [session, setSession] = useState<StudioSessionDetail | null>(null)
  const [sessions, setSessions] = useState<StudioSessionSummary[]>([])
  const [isBeautifying, setIsBeautifying] = useState(false)
  const [previewTab, setPreviewTab] = useState<PreviewTab>('document')
  const [copied, setCopied] = useState(false)

  // Load session list
  useEffect(() => {
    api.studio.sessions.list(50).then(r => setSessions(r.sessions)).catch(() => {})
  }, [])

  // Load session detail
  useEffect(() => {
    if (!sessionId) return
    api.studio.sessions.get(sessionId)
      .then(s => {
        setSession(s)
        if (s.pipelineStatus === 'completed' || s.pipelineStatus === 'suggestions_ready') {
          setView('preview')
        } else if (['analyzing', 'enhancing', 'visualizing', 'assembling'].includes(s.pipelineStatus)) {
          setIsBeautifying(true)
          setView('preview')
        } else {
          setView('preview')
        }
      })
      .catch(() => {
        notify.error('Failed to load session')
        setView('list')
      })
  }, [sessionId])

  const refreshSession = useCallback(async () => {
    if (!sessionId) return
    const s = await api.studio.sessions.get(sessionId)
    setSession(s)
  }, [sessionId])

  const handleSessionCreated = useCallback(async (id: string) => {
    setSessionId(id)
    setIsBeautifying(true)

    // Trigger beautification pipeline (pauses at suggestions_ready)
    try {
      await api.studio.beautify(id)
    } catch {
      // Error handled by progress overlay polling
    }
  }, [])

  const handlePipelineComplete = useCallback(async () => {
    setIsBeautifying(false)
    await refreshSession()
    notify.success('Document analyzed — review image suggestions below')
    // Refresh session list
    api.studio.sessions.list(50).then(r => setSessions(r.sessions)).catch(() => {})
  }, [refreshSession])

  const handlePipelineError = useCallback((message: string) => {
    setIsBeautifying(false)
    notify.error('Beautification failed', { description: message })
    refreshSession()
  }, [refreshSession])

  const handleVisualizationsChange = useCallback((viz: StudioVisualization[]) => {
    setSession(prev => prev ? { ...prev, visualizations: viz } : prev)
  }, [])

  const handleAssembleComplete = useCallback(async () => {
    await refreshSession()
    notify.success('Document assembled with images!')
    api.studio.sessions.list(50).then(r => setSessions(r.sessions)).catch(() => {})
  }, [refreshSession])

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await api.studio.sessions.delete(id)
      setSessions(prev => prev.filter(s => s.id !== id))
      if (sessionId === id) {
        setSessionId(null)
        setSession(null)
        setView('list')
      }
      notify.success('Session deleted')
    } catch {
      notify.error('Failed to delete session')
    }
  }

  const handleCopy = async () => {
    if (!session?.beautifiedText) return
    await navigator.clipboard.writeText(session.beautifiedText)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    notify.success('Copied to clipboard')
  }

  const statusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle2 className="w-4 h-4 text-green-500" />
      case 'suggestions_ready': return <Image className="w-4 h-4 text-indigo-500" />
      case 'failed': return <XCircle className="w-4 h-4 text-red-500" />
      case 'pending': return <Clock className="w-4 h-4 text-gray-400" />
      default: return <Loader2 className="w-4 h-4 text-indigo-500 animate-spin" />
    }
  }

  const statusBadge = (status: string) => {
    if (status === 'completed') return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
    if (status === 'suggestions_ready') return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400'
    if (status === 'failed') return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
    return 'bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-gray-400'
  }

  // ── List View ──
  if (view === 'list') {
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-white/10 flex-shrink-0">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Document Studio</h2>
          <button
            onClick={() => setView('input')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            New Document
          </button>
        </div>

        <div className="flex-1 overflow-auto p-4">
          {sessions.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                No studio sessions yet
              </p>
              <button
                onClick={() => setView('input')}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
              >
                <Plus className="w-4 h-4" />
                Create your first beautified document
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {sessions.map((s) => (
                <div
                  key={s.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSessionId(s.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter') setSessionId(s.id) }}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5 text-left transition-colors group cursor-pointer"
                >
                  {statusIcon(s.pipelineStatus)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {s.inputTitle || 'Untitled Document'}
                    </p>
                    <p className="text-xs text-gray-400">
                      {s.inputWordCount ? `${s.inputWordCount} words` : ''}{s.template ? ` · ${s.template.replace(/_/g, ' ')}` : ''}
                      {' · '}{new Date(s.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className={`px-2 py-0.5 text-xs rounded-full ${statusBadge(s.pipelineStatus)}`}>
                    {s.pipelineStatus === 'suggestions_ready' ? 'images ready' : s.pipelineStatus}
                  </div>
                  <button
                    onClick={(e) => handleDelete(s.id, e)}
                    className="p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  // ── Input View ──
  if (view === 'input') {
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 dark:border-white/10 flex-shrink-0">
          <button
            onClick={() => { setView('list'); setSessionId(null); setSession(null) }}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">New Document</h2>
        </div>
        <div className="flex-1 min-h-0 overflow-auto">
          <StudioInput onSessionCreated={handleSessionCreated} />
        </div>
      </div>
    )
  }

  // ── Preview View ──
  const showImagePanel = session && session.visualizations.length > 0 &&
    (session.pipelineStatus === 'suggestions_ready' || session.pipelineStatus === 'completed')

  const PREVIEW_TABS: { id: PreviewTab; label: string; icon: typeof FileText }[] = [
    { id: 'document', label: 'Document', icon: FileText },
    { id: 'images', label: 'Images', icon: Image },
  ]

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-white/10 flex-shrink-0">
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setView('list'); setSessionId(null); setSession(null) }}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
            {session?.inputTitle || 'Untitled Document'}
          </h2>
          {session?.template && (
            <span className="px-2 py-0.5 text-xs rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
              {session.template.replace(/_/g, ' ')}
            </span>
          )}
          {session?.pipelineStatus === 'suggestions_ready' && (
            <button
              onClick={() => setPreviewTab('images')}
              className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
            >
              Review images &rarr;
            </button>
          )}
        </div>

        {session?.pipelineStatus === 'completed' && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/10 transition-colors"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
            <StudioExportMenu sessionId={session.id} />
          </div>
        )}
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 px-4 border-b border-gray-200 dark:border-white/10 bg-white dark:bg-surface-900 flex-shrink-0">
        {PREVIEW_TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setPreviewTab(id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
              previewTab === id
                ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 min-h-0 overflow-auto">
        {session ? (
          <>
            {previewTab === 'document' && (
              <StudioSideBySide
                originalText={session.inputText}
                beautifiedText={session.beautifiedText}
                beautifiedHtml={session.beautifiedHtml}
              />
            )}

            {previewTab === 'images' && (
              <>
                {showImagePanel && (
                  <StudioImageSuggestions
                    sessionId={session.id}
                    visualizations={session.visualizations}
                    pipelineStatus={session.pipelineStatus}
                    onVisualizationsChange={handleVisualizationsChange}
                    onAssembleComplete={handleAssembleComplete}
                  />
                )}
                <StudioCustomImageGenerator sessionId={session.id} onImageSaved={refreshSession} />
              </>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        )}
      </div>

      {/* Progress overlay */}
      {isBeautifying && sessionId && (
        <StudioProgressOverlay
          sessionId={sessionId}
          onComplete={handlePipelineComplete}
          onError={handlePipelineError}
        />
      )}
    </div>
  )
}
